// The OPAC route tree (public catalogue + member self-service). Code-based routes
// per DEV-GUIDE-002 §4 — a package-composed subtree hand-writes `.lazy()` +
// `createLazyRoute`. This ONE factory hangs BOTH subtrees off the portal's root
// route so router.tsx stays the single wiring point, and it is where the OPAC's
// public-aware split is enforced:
//
//   · CATALOGUE (`/catalogue`) is PUBLIC — CatalogueBrowseLayout does NOT gate.
//     Anonymous visitors render the tab strip + child; whether a given read is
//     served is the SERVER's call (the opacRead publicBrowse gate). A screen maps
//     a 401/403 to a "sign in to browse" state — the layout never redirects.
//   · ACCOUNT (`/account`) is MEMBER-ONLY — AccountLayout re-checks lib:opac via
//     useSession and bounces anonymous → /login (a defence-in-depth mirror of the
//     API's rbac('lib:opac') guard; the UI can only HIDE, the gateway is the
//     authority, ADR-001 §14.2). Signed-in-without-lib:opac → an explicit denied
//     panel, never a blank Outlet.
//
// URL-as-state (DEV-GUIDE-002 §4.1): the keyset list routes carry their filters +
// cursor in the URL through a self-healing validateSearch (zod is not a direct
// portal dep — a hand-written validator with the SAME `.catch()`-style contract:
// garbage heals to the default rather than throwing). loaderDeps→prefetchQuery
// warms the SAME queryOptions the screen re-reads, so the loader and the screen
// share one cache entry.
//
// WARM, NOT AWAITED (audit UX-01, every loader in this file). A loader that
// returned `ensureQueryData(...)` held the route until the API answered: on a
// full load the outlet was EMPTY for the whole wait (no pendingComponent is set,
// so the router's fallback is nothing) and on a nav click the OLD screen stayed
// until the new page arrived. `void prefetchQuery(...)` starts the same fetch
// (same key, same staleTime, deduped against the screen's own read, still fired
// by `defaultPreload: "intent"` on hover) and returns at once, so the route
// commits and the screen paints its heading and its own bones from
// `queryState(...).pending()`. prefetchQuery never throws, which also retires
// the `.catch(() => undefined)` every loader here used to need: a public read
// may 401 (publicBrowse off + anonymous) and a member read runs before the
// layout gate has bounced an anonymous visitor — the failure now lands in the
// cache entry the screen's own read observes, and the screen renders the
// sign-in / redirect state as before.
//
// Screens close their own `.lazy()` loop with `createLazyRoute('/<path>')`.
import { Outlet, createRoute } from "@tanstack/solid-router";
import { lazyRoute } from "@erp/ui/route-kit";
import { libraryQueries } from "../index.js";
import { BOOK_SORTS, BOOK_STATUSES, FINE_STATUSES, HOLD_STATUSES, LOAN_STATUSES } from "../../schemas/index.js";
import type { BookSort, BookStatus, FineStatus, HoldStatus, LoanStatus } from "../../schemas/index.js";
import { ConsoleLayout } from "@erp/ui/console-layout";
import type { AnyRoute } from "@tanstack/solid-router";

/* ─────────────────────────── URL-as-state contracts ─────────────────────────── */

/** Keyset page size — the SAME constant the loader and the screens use, so both hit one cache entry. */
export const OPAC_PAGE_SIZE = 20;
export const OPAC_DEFAULT_SORT: BookSort = "title:asc";

/**
 * The landing's strips (2026-09-22): eight titles newest-published first (two
 * rows of the widest grid) and four ebooks (one row). The loader warms exactly
 * these reads and the landing re-reads them — one cache entry each, as with
 * OPAC_PAGE_SIZE. "Recently published" sorts by the record's publication year
 * because that is what the public list can sort by; it is not an arrivals feed.
 */
export const OPAC_LANDING_BOOKS = 8;
export const OPAC_LANDING_EBOOKS = 4;
export const OPAC_LANDING_SORT: BookSort = "publicationYear:desc";

function optionalString(v: unknown): string | undefined {
  return typeof v === "string" && v.length > 0 ? v : undefined;
}

/**
 * NO `cursor` (2026-09-22). `/catalogue/books` is an INFINITE stream now: the
 * filters still live in the URL — a shared link restores exactly what you were
 * looking at — but the cursor does not, because it no longer names a page. It
 * names a position inside one accumulating list, and a link to "everything up
 * to cursor X" is a link to a different amount of scrolling for every reader.
 * A stale `?cursor=` on an old bookmark simply falls out here, which is the
 * self-healing contract this validator already had.
 */
export interface BooksSearch {
  status?: BookStatus;
  sort?: BookSort;
  /** A subject NAME from the registry — the landing's "Browse by subject" chips land here. */
  subject?: string;
}
export function validateBooksSearch(input: Record<string, unknown>): BooksSearch {
  return {
    status: BOOK_STATUSES.includes(input.status as BookStatus) ? (input.status as BookStatus) : undefined,
    sort: BOOK_SORTS.includes(input.sort as BookSort) ? (input.sort as BookSort) : undefined,
    subject: optionalString(input.subject),
  };
}

/**
 * NO `cursor` (2026-09-22) — `/catalogue/ebooks` is an INFINITE stream too, for
 * the same reason `/catalogue/books` above is: the format filter still lives in
 * the URL, so a shared link restores exactly what you were looking at, but the
 * cursor no longer names a page. It names a position inside one accumulating
 * list, which is a different amount of scrolling for every reader. A stale
 * `?cursor=` on an old bookmark simply falls out here, which is the
 * self-healing contract this validator already had.
 */
export interface EbooksSearch {
  format?: string;
}
export function validateEbooksSearch(input: Record<string, unknown>): EbooksSearch {
  return { format: optionalString(input.format) };
}

/* NO `cursor` on the three account registers (2026-09-22). Each is an INFINITE
 * stream now: the status facet still lives in the URL — a shared or bookmarked
 * link restores the exact view — but the cursor does not, because it no longer
 * names a page. It names a position inside one accumulating list, which is a
 * different amount of scrolling for every reader. A stale `?cursor=` on an old
 * bookmark simply falls out, which is the self-healing contract these
 * validators already had. */
export interface MyLoansSearch {
  status?: LoanStatus;
}
export function validateMyLoansSearch(input: Record<string, unknown>): MyLoansSearch {
  return {
    status: LOAN_STATUSES.includes(input.status as LoanStatus) ? (input.status as LoanStatus) : undefined,
  };
}

export interface MyHoldsSearch {
  status?: HoldStatus;
}
export function validateMyHoldsSearch(input: Record<string, unknown>): MyHoldsSearch {
  return {
    status: HOLD_STATUSES.includes(input.status as HoldStatus) ? (input.status as HoldStatus) : undefined,
  };
}

export interface MyFinesSearch {
  status?: FineStatus;
}
export function validateMyFinesSearch(input: Record<string, unknown>): MyFinesSearch {
  return {
    status: FINE_STATUSES.includes(input.status as FineStatus) ? (input.status as FineStatus) : undefined,
  };
}

/* ─────────────────────────── Catalogue (public) ─────────────────────────── */

/**
 * The public catalogue shell: NO auth gate, and none is added here — anonymous
 * browsing is the point. The tab strip is retired: Browse/Books/Ebooks are
 * perm-less entries in modules/lib's libStudentNav, so the shell sidebar shows
 * them to signed-out visitors too, and the shell owns the <main> landmark.
 */
function CatalogueBrowseLayout() {
  return <Outlet />;
}

/* ─────────────────────────── Account (member) ─────────────────────────── */

/**
 * The member account shell: gates on a live session + lib:opac, then renders the
 * active child. The gating sequence lives in @erp/ui/console-layout — the SAME
 * component the admin consoles use, so "signed out" and "signed in without the
 * grant" behave identically across every portal. The tab strip is retired in
 * favour of the shell sidebar's lib:opac-gated "My account" section.
 */
function AccountLayout() {
  return (
    <ConsoleLayout
      perm="lib:opac"
      deniedMessage="This account is not a library member."
      skeletonId="account-skeleton"
      deniedId="account-denied"
      unreachableId="account-unreachable"
    >
      <Outlet />
    </ConsoleLayout>
  );
}

/* ─────────────────────────────── Factory ─────────────────────────────── */

/**
 * Build the OPAC subtrees under a portal's root route. Returns the routes to
 * spread into `rootRoute.addChildren([...])` (router.tsx): the PUBLIC `/catalogue`
 * tree and the MEMBER-gated `/account` tree, siblings that never touch each other.
 * Screens close their own `.lazy()` loop with `createLazyRoute('/<path>')`.
 */
export function makeOpacRoutes<TParent extends AnyRoute>(rootRoute: TParent) {
  // ── Catalogue (public) ──
  const catalogueRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "catalogue",
    component: CatalogueBrowseLayout,
  });

  // The landing carries no URL state any more (its pager went with the grid);
  // the loader warms the three strips it renders.
  const catalogueIndexRoute = createRoute({
    getParentRoute: () => catalogueRoute,
    path: "/",
    loader: ({ context }) => {
      void context.queryClient.prefetchQuery(libraryQueries.opacBooks({ limit: OPAC_LANDING_BOOKS, sort: OPAC_LANDING_SORT }));
      void context.queryClient.prefetchQuery(libraryQueries.opacEbooks({ limit: OPAC_LANDING_EBOOKS }));
      void context.queryClient.prefetchQuery(libraryQueries.opacSubjects());
    },
  }).lazy(lazyRoute(() => import("../screens/student/CatalogueBrowse.js")));

  const booksRoute = createRoute({
    getParentRoute: () => catalogueRoute,
    path: "books",
    validateSearch: validateBooksSearch,
    loaderDeps: ({ search }) => ({ status: search.status, sort: search.sort, subject: search.subject }),
    // `prefetchInfiniteQuery` warms the FIRST page only (its `pages` default is
    // 1) into the same accumulating entry the screen then reads — so the
    // warm-not-awaited rule above is unchanged, and the reader's scroll fetches
    // page two, never the loader.
    loader: ({ context, deps }) => {
      void context.queryClient.prefetchInfiniteQuery(
          libraryQueries.opacBooksInfinite({
            limit: OPAC_PAGE_SIZE,
            sort: deps.sort ?? OPAC_DEFAULT_SORT,
            status: deps.status,
            subject: deps.subject,
          }),
        );
    },
  }).lazy(lazyRoute(() => import("../screens/student/BookList.js")));

  const bookDetailRoute = createRoute({
    getParentRoute: () => catalogueRoute,
    path: "books/$bookId",
    loader: ({ context, params }) => {
      void context.queryClient.prefetchQuery(libraryQueries.opacBookDetail(params.bookId));
    },
  }).lazy(lazyRoute(() => import("../screens/student/BookDetail.js")));

  const ebooksRoute = createRoute({
    getParentRoute: () => catalogueRoute,
    path: "ebooks",
    validateSearch: validateEbooksSearch,
    loaderDeps: ({ search }) => ({ format: search.format }),
    // `prefetchInfiniteQuery` warms the FIRST page only (its `pages` default is
    // 1) into the same accumulating entry the screen reads — warmed, never
    // awaited, so the route commits at once and the screen paints its bones.
    loader: ({ context, deps }) => {
      void context.queryClient.prefetchInfiniteQuery(libraryQueries.opacEbooksInfinite({ limit: OPAC_PAGE_SIZE, format: deps.format }));
    },
  }).lazy(lazyRoute(() => import("../screens/student/EbookList.js")));

  // ── Account (member) ──
  const accountRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "account",
    component: AccountLayout,
  });

  const accountIndexRoute = createRoute({
    getParentRoute: () => accountRoute,
    path: "/",
    loader: ({ context }) => {
      void context.queryClient.prefetchQuery(libraryQueries.me());
      void context.queryClient.prefetchQuery(libraryQueries.meAccount());
    },
  }).lazy(lazyRoute(() => import("../screens/student/account/AccountOverview.js")));

  const loansRoute = createRoute({
    getParentRoute: () => accountRoute,
    path: "loans",
    validateSearch: validateMyLoansSearch,
    loaderDeps: ({ search }) => ({ status: search.status }),
    // `prefetchInfiniteQuery` warms the FIRST page only (its `pages` default is
    // 1) into the same accumulating entry the screen reads — warmed, never
    // awaited, so the route commits at once and the screen paints its bones.
    loader: ({ context, deps }) => {
      void context.queryClient.prefetchInfiniteQuery(libraryQueries.meLoansInfinite({ limit: OPAC_PAGE_SIZE, status: deps.status }));
    },
  }).lazy(lazyRoute(() => import("../screens/student/account/MyLoans.js")));

  const holdsRoute = createRoute({
    getParentRoute: () => accountRoute,
    path: "holds",
    validateSearch: validateMyHoldsSearch,
    loaderDeps: ({ search }) => ({ status: search.status }),
    loader: ({ context, deps }) => {
      void context.queryClient.prefetchInfiniteQuery(libraryQueries.meHoldsInfinite({ limit: OPAC_PAGE_SIZE, status: deps.status }));
    },
  }).lazy(lazyRoute(() => import("../screens/student/account/MyHolds.js")));

  const finesRoute = createRoute({
    getParentRoute: () => accountRoute,
    path: "fines",
    validateSearch: validateMyFinesSearch,
    loaderDeps: ({ search }) => ({ status: search.status }),
    loader: ({ context, deps }) => {
      void context.queryClient.prefetchInfiniteQuery(libraryQueries.meFinesInfinite({ limit: OPAC_PAGE_SIZE, status: deps.status }));
    },
  }).lazy(lazyRoute(() => import("../screens/student/account/MyFines.js")));

  return [
    catalogueRoute.addChildren([catalogueIndexRoute, booksRoute, bookDetailRoute, ebooksRoute]),
    accountRoute.addChildren([accountIndexRoute, loansRoute, holdsRoute, finesRoute]),
  ];
}
